// CSeatCharacters — 牌桌上三位 AI 牌友的頭像、名字與對話泡泡
// 對應 docs/PRD-Freemium.md 第 8.1 節、src/core/characters.ts（角色與台詞）
//
// 頭像放在各家附近的空位（下家、上家在風位上方，避開門前捨牌放大區；對家在手牌左側），
// 對話泡泡朝牌桌中央冒出，停留幾秒後淡出；同一家新的一句會取代舊的。
//
// === 使用圖片 ===
// - public/assets/avatars/<角色 id>.png（key: avatarKey(id)）— 九位牌友頭像（Codex 產圖，圓形去背）
// 泡泡、名牌為純程式繪製（Phaser.Graphics＋Text）

import type Phaser from 'phaser';
import type { ICharacter } from '@core/characters';
import { Seat, type TSeat } from '@core/types';
import { PAGE_THEME } from './page-skin';

export const avatarKey = (id: string): string => `avatar-${id}`;

// ---- 版面常數 ----
const AVATAR_SIZE = 72; // 頭像直徑
const NAME_FONT_SIZE = 20;
const NAME_OFFSET_Y = 48; // 名字在頭像下方
const NAME_BG = 0x2a1206;
const NAME_BG_ALPHA = 0.75;
const NAME_PAD_X = 10;
const NAME_PAD_Y = 3;
const BUBBLE_FONT_SIZE = 24;
const BUBBLE_MAX_WIDTH = 300;
const BUBBLE_PAD_X = 16;
const BUBBLE_PAD_Y = 10;
const BUBBLE_RADIUS = 14;
const BUBBLE_FILL = 0xfffbef;
const BUBBLE_STROKE = 0xe7c26b;
const BUBBLE_TEXT = '#3a2a1a';
const BUBBLE_SHOW_MS = 2600; // 泡泡停留
const BUBBLE_FADE_MS = 260;
const BUBBLE_POP_SCALE = 0.6; // 彈出起始縮放
const DEPTH = 45; // 在手牌、海底之上；儀式橫幅之下

// 各家頭像中心與泡泡錨點（泡泡往牌桌中央方向長）
const SPOTS: Record<
  Exclude<TSeat, typeof Seat.Self>,
  { x: number; y: number; bubbleX: number; bubbleY: number; originX: number }
> = {
  [Seat.Right]: { x: 1030, y: 240, bubbleX: 980, bubbleY: 230, originX: 1 }, // 避開右上花牌與「思考中」
  [Seat.Opposite]: { x: 300, y: 64, bubbleX: 345, bubbleY: 140, originX: 0 },
  [Seat.Left]: { x: 180, y: 258, bubbleX: 230, bubbleY: 246, originX: 0 }, // 避開左上花牌
};

export class CSeatCharacters {
  private readonly bubbles = new Map<TSeat, Phaser.GameObjects.Container>();
  private readonly objects: Phaser.GameObjects.GameObject[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly characters: ReadonlyMap<TSeat, ICharacter>
  ) {
    for (const [seat, character] of characters) {
      if (seat === Seat.Self) continue;
      const spot = SPOTS[seat];
      const key = avatarKey(character.id);
      if (scene.textures.exists(key))
        this.objects.push(
          scene.add
            .image(spot.x, spot.y, key)
            .setDisplaySize(AVATAR_SIZE, AVATAR_SIZE)
            .setDepth(DEPTH)
        );
      const name = scene.add
        .text(spot.x, spot.y + NAME_OFFSET_Y, character.name, {
          color: '#ffe9a8',
          fontFamily: PAGE_THEME.font,
          fontSize: `${NAME_FONT_SIZE}px`,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 1);
      const plate = scene.add
        .rectangle(
          spot.x,
          spot.y + NAME_OFFSET_Y,
          name.width + NAME_PAD_X * 2,
          name.height + NAME_PAD_Y * 2,
          NAME_BG,
          NAME_BG_ALPHA
        )
        .setDepth(DEPTH);
      this.objects.push(plate, name);
    }
  }

  character(seat: TSeat): ICharacter | undefined {
    return this.characters.get(seat);
  }

  say(seat: TSeat, text: string): void {
    if (seat === Seat.Self || !text) return;
    this.bubbles.get(seat)?.destroy();
    const spot = SPOTS[seat];
    const label = this.scene.add
      .text(0, 0, text, {
        color: BUBBLE_TEXT,
        fontFamily: PAGE_THEME.font,
        fontSize: `${BUBBLE_FONT_SIZE}px`,
        fontStyle: 'bold',
        wordWrap: { width: BUBBLE_MAX_WIDTH, useAdvancedWrap: true },
      })
      .setOrigin(spot.originX, 0.5);
    const width = label.width + BUBBLE_PAD_X * 2;
    const height = label.height + BUBBLE_PAD_Y * 2;
    const left = spot.originX === 1 ? -width : 0;
    label.setX(spot.originX === 1 ? -BUBBLE_PAD_X : BUBBLE_PAD_X);
    const bg = this.scene.add.graphics();
    bg.fillStyle(BUBBLE_FILL, 1);
    bg.fillRoundedRect(left, -height / 2, width, height, BUBBLE_RADIUS);
    bg.lineStyle(3, BUBBLE_STROKE);
    bg.strokeRoundedRect(left, -height / 2, width, height, BUBBLE_RADIUS);
    const bubble = this.scene.add
      .container(spot.bubbleX, spot.bubbleY, [bg, label])
      .setDepth(DEPTH + 2)
      .setScale(BUBBLE_POP_SCALE);
    this.bubbles.set(seat, bubble);
    this.scene.tweens.add({ targets: bubble, scale: 1, duration: 180, ease: 'Back.easeOut' });
    this.scene.tweens.add({
      targets: bubble,
      alpha: 0,
      delay: BUBBLE_SHOW_MS,
      duration: BUBBLE_FADE_MS,
      onComplete: () => {
        if (this.bubbles.get(seat) === bubble) this.bubbles.delete(seat);
        bubble.destroy();
      },
    });
  }

  destroy(): void {
    for (const bubble of this.bubbles.values()) bubble.destroy();
    this.bubbles.clear();
    for (const object of this.objects) object.destroy();
    this.objects.length = 0;
  }
}
